/**
 * Accessible names for portfolio post images and links (EPD-12471).
 *
 * A row of links that all read "View post on Instagram" is indistinguishable
 * to a screen-reader user scanning links, and an empty alt tells them nothing
 * about the picture. These helpers build names that say whose post it is and
 * tell posts apart.
 */

/** Longest caption excerpt put into alt text, in characters. */
export const ALT_CAPTION_MAX = 80;

/**
 * The first `max` characters of a caption on a word boundary, with an
 * ellipsis when cut. Whitespace (line breaks, runs of spaces) collapses.
 */
export function captionExcerpt(
  caption: string | null | undefined,
  max: number = ALT_CAPTION_MAX,
): string {
  const text = (caption ?? "").replace(/\s+/g, " ").trim();
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  const head = lastSpace > max / 2 ? cut.slice(0, lastSpace) : cut;
  return `${head.replace(/[\s.,;:!?-]+$/, "")}…`;
}

/**
 * Alt text for a post thumbnail: "{Platform} post by {creator}", plus a short
 * caption excerpt when one is given. Leave the caption out when it is already
 * shown next to the image, so it isn't read twice.
 */
export function postImageAlt({
  platformLabel,
  creatorName,
  caption,
}: {
  platformLabel: string;
  creatorName?: string | null;
  caption?: string | null;
}): string {
  const name = creatorName?.trim();
  const base = name
    ? `${platformLabel} post by ${name}`
    : `${platformLabel} post`;
  const excerpt = captionExcerpt(caption);
  return excerpt ? `${base}: ${excerpt}` : base;
}

/**
 * A post's publish time as a date ("Oct 3, 2026"), from a Unix timestamp in
 * seconds (the legacy feed's `postedTs`); millisecond values are accepted
 * too. Null for a missing or invalid value. Formatted in UTC so the name is
 * the same wherever it is rendered.
 */
export function formatPostDate(ts: number | null | undefined): string | null {
  if (ts == null || !Number.isFinite(ts) || ts <= 0) return null;
  const ms = ts > 1e12 ? ts : ts * 1000;
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * The visually-hidden end of a post link's name: "View Instagram post from
 * Oct 3, 2026". Without a date, the post's 1-based position in the row
 * ("View Instagram post 3") keeps the links distinct. When another post in the
 * row has the same date (`dateShared`), the position is added too ("View
 * Instagram post 2 from Oct 3, 2026").
 */
export function postLinkLabel({
  platformLabel,
  postedTs,
  position,
  dateShared = false,
}: {
  platformLabel: string;
  postedTs?: number | null;
  position: number;
  dateShared?: boolean;
}): string {
  const date = formatPostDate(postedTs);
  if (!date) return `View ${platformLabel} post ${position}`;
  return dateShared
    ? `View ${platformLabel} post ${position} from ${date}`
    : `View ${platformLabel} post from ${date}`;
}

/**
 * The link names for a row of posts, in row order (see postLinkLabel). Posts
 * that share a date get their position as well, so no two names match.
 */
export function postLinkLabels(
  platformLabel: string,
  postedTs: ReadonlyArray<number | null | undefined>,
): string[] {
  const dates = postedTs.map(formatPostDate);
  const perDate = new Map<string, number>();
  for (const date of dates) {
    if (date) perDate.set(date, (perDate.get(date) ?? 0) + 1);
  }
  return postedTs.map((ts, i) => {
    const date = dates[i];
    return postLinkLabel({
      platformLabel,
      postedTs: ts,
      position: i + 1,
      dateShared: date != null && (perDate.get(date) ?? 0) > 1,
    });
  });
}

/**
 * The name of a link to a brand collaboration post: "View {Brand}
 * collaboration post on {host}", numbered when the brand has several posts so
 * each link is distinct.
 */
export function collaborationPostLabel({
  brandName,
  host,
  index,
  total,
}: {
  brandName: string;
  host: string;
  index: number;
  total: number;
}): string {
  const brand = brandName.trim();
  const subject = brand ? `${brand} collaboration post` : "collaboration post";
  const numbered = total > 1 ? `${subject} ${index + 1} of ${total}` : subject;
  return `View ${numbered} on ${host}`;
}
